import React, { useState } from "react";
import { Table, Skeleton, Empty, Tag, Pagination, Popconfirm, Button } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import useAxiosInstance from "../../lib/useAxiosInstance";

const PowerBiList = () => {
  const axiosInstance = useAxiosInstance();
  const queryClient = useQueryClient();

  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [deletingId, setDeletingId] = useState(null);

  const fetchPowerBiLinks = async () => {
    const res = await axiosInstance.get(
      `/api/powerbi-module/getAllPowerbiLinks?page=${currentPage}&limit=${limit}`
    );
    const result = res?.data || {};
    setTotal(result?.totalPowerBiLinks || 0);
    return result?.data || [];
  };

  const { data = [], isLoading } = useQuery(
    ["powerbi-links", currentPage, limit],
    fetchPowerBiLinks,
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    }
  );

  const handleDelete = async (record) => {
    try {
      setDeletingId(record._id);
      await axiosInstance.delete(`/api/powerbi-module/deletePowerbiLink/${record._id}`);
      toast.success("Report deleted successfully");
      queryClient.invalidateQueries(["powerbi-links"]);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to delete report");
    } finally {
      setDeletingId(null);
    }
  };

  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (type) => (type === "sir" ? <Tag color="orange">SIR Report</Tag> : <Tag color="blue">Power BI Link</Tag>),
    },
    {
      title: "Link",
      dataIndex: "link",
      key: "link",
      render: (text, record) =>
        record.type === "sir" ? (
          <span>HTML File</span>
        ) : (
          <a href={text} target="_blank" rel="noopener noreferrer">
            {text}
          </a>
        ),
    },
    {
      title: "Clients",
      dataIndex: "clientIds",
      key: "clientIds",
      render: (clients) =>
        clients?.length > 0 ? (
          clients.map((client) => (
            <Tag key={client._id}>
              {client?.adminFirstName} {client?.adminLastName}
            </Tag>
          ))
        ) : (
          <span>-</span>
        ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (text) => text || "-",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Popconfirm
          title="Delete this report?"
          description="This action cannot be undone."
          okText="Yes"
          cancelText="No"
          onConfirm={() => handleDelete(record)}
        >
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            loading={deletingId === record._id}
          >
            Delete
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      <Table
        rowKey="_id"
        dataSource={data}
        columns={columns}
        pagination={false}
        loading={isLoading}
        locale={{
          emptyText: isLoading ? (
            <Skeleton active paragraph={{ rows: 4 }} />
          ) : (
            <Empty description="No Power BI Links" />
          ),
        }}
      />

      <div className="report-pagination mt-4 flex justify-between items-center">
        <div>
          <span className="mr-2">Entries per page:</span>
          <select
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            {[10, 20, 50, 100].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>

        <Pagination
          current={currentPage}
          pageSize={limit}
          total={total}
          onChange={(page, pageSize) => {
            setCurrentPage(page);
            setLimit(pageSize);
          }}
          showSizeChanger={false}
        />
      </div>
    </div>
  );
};

export default PowerBiList;
